// A Computer player's decision-making for 501/x01: what to aim at next, and
// working an entire turn's worth of simulated darts into a single plan the
// controller can commit in one of its three real outcomes (bust / checkout /
// normal - see x01-game-controller.js's bustTurn/finishTurn/reduceTurn).
//
// The loop this drives is exactly "decide an aim point -> throw -> decide
// the next aim point using where that dart actually landed -> throw -> ...":
// decideNextTarget() only ever looks at the *current* remaining, never a
// pre-planned route, so a dart that misses its intended treble genuinely
// changes what the next one aims for, the same way a real player re-reads
// the board after every throw rather than committing to a plan from before
// the turn started.
import { checkoutFor, isBust } from './x01-game.js';
import { simulateThrow } from '../../core/ai-throw-simulator.js';

// checkoutFor() returns a route like "T20 T20 Bull" or, for a handful of
// totals, two alternatives separated by " / " - the first listed route is
// always the conventional one a real player would actually throw (see its
// own comment in x01-game.js), so that's the one an AI follows too.
function firstCheckoutToken(remaining) {
    const route = checkoutFor(remaining);
    if (!route) return null;
    return route.split(' / ')[0].split(' ')[0];
}

// "T20" -> treble 20, "D16" -> double 16, "S1" -> (outer-bed) single 1,
// "Bull" -> the bullseye (50) - the only bull token this chart ever uses,
// since the outer bull (25) is never part of a real checkout route (see
// x01-game.js's own CHECKOUT_DART_VALUES comment).
function parseCheckoutToken(token) {
    if (token === 'Bull') return { number: 25, ring: 'bullseye' };
    const ring = { T: 'triple', D: 'double', S: 'single' }[token[0]];
    const number = parseInt(token.slice(1), 10);
    return { number, ring };
}

// What a good player aims at next, given nothing but the current remaining -
// stateless on purpose, so calling it fresh after every single dart (rather
// than committing to a whole turn's plan up front) is exactly how this is
// meant to be used.
//
// Always aims at the next step of a real checkout route the moment one
// exists at all (x01-game.js's checkoutFor), even when the *whole* route
// can't be completed with however many darts are left in this turn -
// there's no need for it to, since this gets called fresh before every
// dart anyway; taking one genuine step toward the route now is enough, and
// next turn picks up from wherever that left the remaining. An earlier
// version only did this once the full route fit in the darts left (gating
// on minDartsForCheckout), which meant a remaining like 46 with only 1 dart
// left in the turn - checkoutFor(46) is "S10 D18", a genuine 2-dart route -
// fell back to treble 20 instead: a guaranteed bust (46 - 60 < 0) it had no
// reason to risk, when the route's own first step (S10) leaves 36, no bust
// at all. Treble 20 remains the default for any remaining with no checkout
// route at all - checkoutFor returns null either far from checkout range,
// or for one of the handful of true "bogey" totals (166, 169, ...) no route
// can ever reach from, where there's genuinely no safer alternative target
// this simple a policy can offer.
export function decideNextTarget({ remaining }) {
    const token = firstCheckoutToken(remaining);
    if (token) return parseCheckoutToken(token);
    return { number: 20, ring: 'triple' };
}

// Plans a Computer player's entire turn: up to 3 darts, stopping early on a
// genuine bust or a genuine checkout (a real double or the bullseye - see
// the AI implementation plan's correction: zeroing out on anything else,
// like a single or the outer bull, is a bust here, not a win, even though
// x01-game-controller.js's own confirmTurn() can't tell the difference for
// a human's typed total). Returns everything the controller needs to commit
// the result and everything the UI needs to animate it dart by dart:
//   - throws: [{ aim, aimPoint, actualPoint, result }, ...] in throw order
//   - totalScore: the sum of every dart actually thrown this turn
//   - dartsUsed: how many darts it took (3, unless it busted/checked out early)
//   - outcome: 'bust' | 'checkout' | 'normal'
export function simulateAiTurn({ remaining, throwProfile }) {
    const throws = [];
    let scratchRemaining = remaining;
    let outcome = 'normal';

    while (throws.length < 3) {
        const target = decideNextTarget({ remaining: scratchRemaining });
        const thrown = simulateThrow(target, throwProfile);
        throws.push(thrown);

        const score = thrown.result.score;
        if (isBust(scratchRemaining, score)) {
            outcome = 'bust';
            break;
        }
        const after = scratchRemaining - score;
        if (after === 0) {
            const validFinish = thrown.result.ring === 'double' || thrown.result.ring === 'bullseye';
            outcome = validFinish ? 'checkout' : 'bust';
            break;
        }
        scratchRemaining = after;
    }

    const totalScore = throws.reduce((sum, t) => sum + t.result.score, 0);
    return { throws, totalScore, dartsUsed: throws.length, outcome };
}
